import DefaultPostponeOrderButtons from '@/components/OrdersModule/OrderDetail/Footer/AcceptedOrderDetailFooter/PostponeOrderTimeSelector/DefaultPostponeOrderButtons'
import { postponeOrderAvailableShiftValues } from '@/constants/order-flow-action-time-values'
import Chip from '@/features/ui/components/Chip'
import { useAppSelector } from '@/hooks/redux'
import { useScreenLayout } from '@/hooks/screen'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { ICustomPostponeOrderTime } from '@/types/customPostponeOrderTime'
import { PostponeOrderTimeValues } from '@/types/order-flow-action-time-values'
import { calculatePostponedUntil, getPostponementTimeAsDuration } from '@/utils/postponeOrderTime'
import dayjs, { Dayjs } from 'dayjs'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, Text, View } from 'react-native'

interface DefaultPostponeOrderTimeSelectorProps {
    orderUuid: string
    postponementTime: PostponeOrderTimeValues | undefined
    customTime: ICustomPostponeOrderTime
    currentTime: Dayjs
    onTimePress: (value: PostponeOrderTimeValues) => void
    onBackButton: () => void
    onPostponeButton: () => Promise<void>
}

const DefaultPostponeOrderTimeSelector: FC<DefaultPostponeOrderTimeSelectorProps> = ({
    orderUuid,
    postponementTime,
    customTime,
    currentTime,
    onTimePress,
    onBackButton,
    onPostponeButton,
}) => {
    const order = useAppSelector(ordersSelectors.orderByUuid(orderUuid))

    const screenLayout = useScreenLayout()

    const { t } = useTranslation()

    if (!order) {
        return null
    }

    const checkDefaultTimeDisabled = (
        postponementTime: Exclude<PostponeOrderTimeValues, 'custom'>
    ) => {
        const durationTime = dayjs.duration({
            hours: postponementTime,
        })

        return calculatePostponedUntil(order, durationTime).isBefore(currentTime)
    }

    const checkIsDisablePostponeButton = () => {
        if (!postponementTime) {
            return true
        }

        const durationTime = getPostponementTimeAsDuration(postponementTime, customTime)

        return calculatePostponedUntil(order, durationTime).isBefore(currentTime)
    }

    const postponeButtonDisabled = checkIsDisablePostponeButton()

    const isCompactLayout = screenLayout === 'compact'

    return (
        <>
            <Text style={[styles.title, isCompactLayout && { fontSize: 13.5 }]}>
                {t('order.detail.postponeTitle')}
            </Text>
            <View style={styles.container}>
                {postponeOrderAvailableShiftValues.map((value, index) => {
                    if (value === 'custom') {
                        return (
                            <Chip
                                key={index}
                                label={t('order.detail.customTime')}
                                subText={
                                    `${customTime.hours > 0 ? `${customTime.hours} HOD ` : ``}` +
                                    `${customTime.minutes > 0 ? `${customTime.minutes} MIN` : ``}`
                                }
                                active={postponementTime === 'custom'}
                                onPress={() => onTimePress(value)}
                                style={isCompactLayout && { width: '62%' }}
                            />
                        )
                    }
                    const disabled = checkDefaultTimeDisabled(value)
                    return (
                        <Chip
                            key={index}
                            label={`${value} hod`}
                            disabled={disabled}
                            active={postponementTime === value}
                            onPress={() => onTimePress(value)}
                            style={isCompactLayout && { width: '30%' }}
                        />
                    )
                })}
            </View>
            <DefaultPostponeOrderButtons
                onBack={onBackButton}
                postponeButtonDisabled={postponeButtonDisabled}
                onPostpone={onPostponeButton}
            />
        </>
    )
}

const styles = StyleSheet.create({
    container: {
        flexWrap: 'wrap',
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 12,
        gap: 8,
    },
    title: {
        fontSize: 16,
        marginBottom: 12,
        textAlign: 'center',
    },
})

export default DefaultPostponeOrderTimeSelector
